import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
  Box,
  Text,
  useToast,
  VStack,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Spinner,
  Image,
  Heading,
  Container,
  Center
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import logo from './High res brand face logo.png';
import moment from "moment";

// Add color theme constants
const COLORS = {
    isabelline: "#F4EEEA",    // Background, card backgrounds
    maastrichtBlue: "#0E1735", // Headings, important text
    shadowBlue: "#7481AF",    // Secondary elements, borders, labels
    mandarin: "#F17946",      // CTAs, highlights, errors
    shinyShamrock: "#56A27F"  // Success states, secondary actions
};

export const SuccessPage = () => {
    const { registrationID } = useParams();
    const [registrationData, setRegistrationData] = useState(null);
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRegistration = async () => {
          setLoading(true);
          try {
            const { data, error } = await supabase
              .from("full_registration_details_with_enrollment")
              .select(
                "id, participant_name, participant_email, name, start_date, end_date, price, course_run_id, registration_type, registration_stage, billing_type, birth_age, nationality,course_dates,course_duration, payment_balance, ssg_claim_doc_submitted"
              )
              .gte("start_date", new Date().toLocaleDateString('fr-CA')) //prevent payment to past courses
              .eq("id", registrationID)
              .single();
    
            if (error) throw error;
            
            // Check if user should be on this success page
            if (!(data.payment_balance === 0)) {
              // Redirect back to enrollment page if conditions not met
              navigate(`/enrollment/${registrationID}`);
              return;
            }
            
            setRegistrationData(data);

            // Track purchase event in Google Analytics with fixed price
            if (window.gtag) {
                window.gtag('event', 'purchase', {
                    transaction_id: registrationID,
                    value: 1050.00,
                    currency: 'SGD',
                    items: [{
                        item_id: data.course_run_id,
                        item_name: data.name,
                        price: 1050.00
                    }]
                });
                console.log('Google Analytics purchase event tracked');
            }

            // Track purchase event in Meta Pixel with fixed price
            if (window.fbq) {
                window.fbq('track', 'Purchase', {
                    value: 1050.00,
                    currency: 'SGD'
                });
                console.log('Meta Pixel purchase event tracked');
            }

          } catch (error) {
            console.error("Error fetching registration:", error);
            toast({
              title: "Error",
              description: "Could not load registration details.",
              status: "error",
              isClosable: true,
              position: "top",
            });
          } finally {
            setLoading(false);
          }
        };
    
        fetchRegistration();
      }, [registrationID, toast, navigate]);

    return (
        loading ? (
            <Center h="100vh" bg={COLORS.isabelline}>
                <Spinner size="xl" thickness="4px" speed="0.65s" color={COLORS.mandarin} />
            </Center>
        ) : !registrationData ? (
            <Center h="100vh" bg={COLORS.isabelline}>
                <Text color={COLORS.maastrichtBlue}>No registration data found.</Text>
            </Center>
        ) : (
            <Box
                w={"100%"} 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                bg={COLORS.isabelline} 
                minH="100vh" 
                py={8}
            >
                <Box 
                    width="100%" 
                    bg="white" 
                    px={10}
                    py={10}
                    borderRadius="lg" 
                    boxShadow="md"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <VStack spacing={8} w="full">
                            <motion.div
                                initial={{ y: -50 }}
                                animate={{ y: 0 }}
                                transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
                            >
                                <Heading size={"sm"} color={COLORS.maastrichtBlue}
                                         borderBottom="2px solid"
                                         borderColor={COLORS.mandarin}
                                         pb={2}
                                >
                                    Enrollment confirmed
                                </Heading>
                            </motion.div>

                            <motion.div
                                initial={{ x: 100 }}
                                animate={{ x: 0 }}
                                transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                            >
                                <Text fontSize="md" color={COLORS.maastrichtBlue}>
                                    Thank you for your payment, {registrationData?.participant_name}. Your enrollment for this course is confirmed.
                                </Text>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
                            >
                                <Text fontSize="md" color={COLORS.maastrichtBlue}>
                                    You will receive the signed student contract via your email <span style={{color: COLORS.mandarin, fontWeight: "bold"}}>{registrationData.participant_email}</span> soon.
                                </Text>
                                <br/>
                                <Text fontSize="md" fontWeight="bold" color={COLORS.maastrichtBlue} textAlign="center">
                                    See you for the course!
                                </Text>
                            </motion.div>

                            {/* Course Details Section */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.6, duration: 1, ease: "easeOut" }}
                            >
                                <Box
                                    bg={COLORS.isabelline}
                                    border="1px solid"
                                    borderColor={COLORS.shadowBlue}
                                    borderRadius="md"
                                    p={6}
                                    boxShadow="sm"
                                >
                                    <Text fontSize="lg" fontWeight="bold" mb={4} color={COLORS.maastrichtBlue}>
                                        {registrationData?.name}
                                    </Text>
                                    <Table variant="simple" size="sm" width="100%" bg="white">
                                        <Thead bg={COLORS.isabelline}>
                                            <Tr>
                                                <Th textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg={COLORS.isabelline} color={COLORS.maastrichtBlue}>Date</Th>
                                                <Th textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg={COLORS.isabelline} color={COLORS.maastrichtBlue}>Start Time</Th>
                                                <Th textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg={COLORS.isabelline} color={COLORS.maastrichtBlue}>End Time</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody bg="white">
                                            {registrationData?.course_dates.map((session, index) => (
                                                <Tr key={index} bg="white">
                                                    <Td textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg="white" color={COLORS.maastrichtBlue}>
                                                        {moment(session.date, "DD.MM.YYYY").format("DD MMM YYYY (ddd)")}
                                                    </Td>
                                                    <Td textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg="white" color={COLORS.maastrichtBlue}>
                                                        {moment(session.start_time, "HHmm").format("h:mm A")}
                                                    </Td>
                                                    <Td textAlign="center" border="1px solid" borderColor={COLORS.shadowBlue} bg="white" color={COLORS.maastrichtBlue}>
                                                        {moment(session.end_time, "HHmm").format("h:mm A")}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                    <Text mt={4} textAlign="left" color={COLORS.maastrichtBlue}>
                                        <b>Venue:</b> Habitat Learning Centre, 1 Goldhill Plaza, #02-27, Singapore 308899. The nearest MRT is Novena.
                                    </Text>
                                    <Text mt={4} textAlign="left" color={COLORS.maastrichtBlue}>
                                        <b>Directions:</b>{" "}
                                        <a
                                            href="https://sozcode.com/goldhill-plaza-how-to-get-there"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: COLORS.mandarin, textDecoration: "underline" }}
                                        >
                                            https://sozcode.com/goldhill-plaza-how-to-get-there
                                        </a>
                                    </Text>
                                    <Text mt={4} textAlign="left" color={COLORS.maastrichtBlue}>
                                        <b>Duration:</b> {registrationData?.course_duration} hours
                                    </Text>
                                </Box>
                            </motion.div>
                        </VStack>
                    </motion.div>
                </Box>
            </Box>
        )
    );
};

export default SuccessPage;